import { useEffect, useState } from "react";
import { ChevronDown, ListTree } from "lucide-react";

/** Reads every `h2[id]` inside #legal-article once mounted; tracks the one in view. */
const useLegalSections = () => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => {
    const root = document.getElementById("legal-article");
    if (!root) return undefined;
    const heads = Array.from(root.querySelectorAll("h2[id]"));
    setItems(heads.map((h) => ({ id: h.id, text: h.textContent })));
    if (heads[0]) setActive(heads[0].id);
    const io = new IntersectionObserver(
      (entries) => {
        const seen = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (seen[0]) setActive(seen[0].target.id);
      },
      { rootMargin: "-15% 0px -70% 0px", threshold: 0 },
    );
    heads.forEach((h) => io.observe(h));
    return () => io.disconnect();
  }, []);
  return { items, active };
};

const jump = (e, id) => {
  e.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  window.history.replaceState(null, "", `#${id}`);
};

/** Sticky side index (xl+). Pair with <article id="legal-article"> and h2 `scroll-mt-28`. */
export const LegalToc = ({ testid = "legal-toc" }) => {
  const { items, active } = useLegalSections();
  if (!items.length) return <div className="hidden xl:block" />;
  return (
    <nav aria-label="On this page" data-testid={testid} className="hidden self-start xl:sticky xl:top-28 xl:block xl:max-h-[calc(100vh-8rem)] xl:overflow-y-auto xl:pr-2">
      <p className="text-[13px] font-semibold uppercase tracking-[0.24em] text-olive">On this page</p>
      <ol className="mt-4 space-y-0.5 border-l border-border">
        {items.map((it, i) => {
          const on = it.id === active;
          return (
            <li key={it.id}>
              <a href={`#${it.id}`} onClick={(e) => jump(e, it.id)} data-testid={`${testid}-link-${it.id}`} aria-current={on ? "true" : undefined}
                className={`-ml-px block border-l-2 py-1.5 pl-4 text-[13px] leading-snug transition-colors ${on ? "border-tangerine font-semibold text-ink" : "border-transparent text-olive hover:border-ink/30 hover:text-ink"}`}>
                <span className="mr-2 font-serif text-[13px] tabular-nums text-olive/70">{String(i + 1).padStart(2, "0")}</span>{it.text}
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

/** Collapsible index for narrower screens, placed just under the page header. */
export const LegalTocMobile = ({ testid = "legal-toc-mobile" }) => {
  const { items } = useLegalSections();
  if (!items.length) return null;
  return (
    <details data-testid={testid} className="group mt-6 rounded-xl border border-border bg-bone xl:hidden">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-3.5 text-base font-semibold text-ink">
        <span className="inline-flex items-center gap-2"><ListTree className="h-4 w-4 text-tangerine" strokeWidth={2.2} /> On this page</span>
        <ChevronDown className="h-4 w-4 text-olive transition-transform group-open:rotate-180" />
      </summary>
      <ol className="border-t border-border px-5 py-3">
        {items.map((it, i) => (
          <li key={it.id}>
            <a href={`#${it.id}`} onClick={(e) => jump(e, it.id)} className="block py-1.5 text-base text-olive hover:text-ink">
              <span className="mr-2 font-serif text-[13px] tabular-nums text-olive/70">{String(i + 1).padStart(2, "0")}</span>{it.text}
            </a>
          </li>
        ))}
      </ol>
    </details>
  );
};
